import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AppLayout } from '@/components/layout/AppLayout';
import { RequireBusiness } from '@/components/RequireBusiness';
import { useApp } from '@/contexts/AppContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Camera, Upload, Loader2 } from 'lucide-react';
import { isValidGoogleUrl } from '@/lib/urlValidation';

const tabs = [
  { id: 'business', label: 'Business' },
  { id: 'seo', label: 'SEO' },
  { id: 'defaults', label: 'Defaults' },
  { id: 'users', label: 'Users' },
  { id: 'subscription', label: 'Plan' },
];

export default function SettingsPage() {
  return (
    <RequireBusiness>
      <SettingsContent />
    </RequireBusiness>
  );
}

function SettingsContent() {
  const { business, updateBusiness, user } = useApp();
  const [activeTab, setActiveTab] = useState('business');
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    category: '',
    location: '',
    service_area: '',
    google_profile_url: '',
    logo_url: '',
  });

  useEffect(() => {
    if (business) {
      setFormData({
        name: business.name || '',
        industry: business.industry || '',
        category: business.category || '',
        location: business.location || '',
        service_area: business.service_area || '',
        google_profile_url: business.google_profile_url || '',
        logo_url: (business as any).logo_url || '',
      });
    }
  }, [business]);

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/logo-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('business-logos')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('business-logos')
        .getPublicUrl(fileName);

      setFormData(prev => ({ ...prev, logo_url: publicUrl }));
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    // Validate Google Profile URL if provided
    if (formData.google_profile_url && !isValidGoogleUrl(formData.google_profile_url)) {
      toast.error('Please enter a valid Google Business Profile URL (e.g., https://g.page/your-business)');
      return;
    }
    
    setSaving(true);
    const { error } = await updateBusiness(formData);
    setSaving(false);
    
    if (!error) {
      toast.success('Settings saved!');
    }
  };

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <h1 className="section-title mb-4 sm:mb-6">Settings</h1>

        {/* Tabs - Horizontal scrollable on mobile */}
        <div className="mb-6 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-1 overflow-x-auto hide-scrollbar pb-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-4 py-2 text-sm font-medium whitespace-nowrap rounded-xl
                  transition-colors flex-shrink-0
                  ${activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="soft-card p-4 sm:p-6">
          {activeTab === 'business' && (
            <div className="space-y-6">
              <h2 className="text-lg font-medium">Business Information</h2>
              
              {/* Logo Upload */}
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <div className="relative">
                  <div 
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-muted border-2 border-dashed border-border flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {formData.logo_url ? (
                      <img 
                        src={formData.logo_url} 
                        alt="Business logo" 
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <Camera className="w-8 h-8 text-muted-foreground" />
                    )}
                    {uploadingLogo && (
                      <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-sm font-medium">Business Logo</Label>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Upload your business logo (max 2MB)
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                  >
                    <Upload size={14} className="mr-2" />
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Business Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="h-11"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Industry</Label>
                  <Input
                    value={formData.industry}
                    onChange={(e) => handleChange('industry', e.target.value)}
                    className="h-11"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Category</Label>
                  <Input
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    className="h-11"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    className="h-11"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Service Area</Label>
                  <Input
                    value={formData.service_area}
                    onChange={(e) => handleChange('service_area', e.target.value)}
                    className="h-11"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Google Profile URL</Label>
                  <Input
                    value={formData.google_profile_url}
                    onChange={(e) => handleChange('google_profile_url', e.target.value)}
                    className="h-11"
                    placeholder="https://g.page/..."
                  />
                </div>
              </div>

              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}

          {activeTab === 'seo' && (
            <div className="space-y-4">
              <h2 className="text-lg font-medium">SEO Keywords</h2>
              <p className="text-sm text-muted-foreground">
                Define default keywords that will be suggested when generating reviews.
              </p>
              <div>
                <Label className="text-sm font-medium mb-2 block">Primary Keywords</Label>
                <Input
                  placeholder="quality care, expert team, professional service"
                  className="h-11"
                />
                <p className="text-xs text-muted-foreground mt-1">Separate with commas</p>
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Secondary Keywords</Label>
                <Input
                  placeholder="affordable, convenient, trusted"
                  className="h-11"
                />
                <p className="text-xs text-muted-foreground mt-1">Separate with commas</p>
              </div>
              <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground">
                Save Keywords
              </Button>
            </div>
          )}

          {activeTab === 'defaults' && (
            <div className="space-y-4">
              <h2 className="text-lg font-medium">Default Review Settings</h2>
              <p className="text-sm text-muted-foreground">
                Set default values for review generation.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Default Tone</Label>
                  <select className="w-full h-11 rounded-lg border border-input bg-background px-3 text-sm">
                    <option>Professional</option>
                    <option>Friendly</option>
                    <option>Enthusiastic</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Default Style</Label>
                  <select className="w-full h-11 rounded-lg border border-input bg-background px-3 text-sm">
                    <option>Narrative</option>
                    <option>Testimonial</option>
                    <option>Conversational</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Default Word Limit</Label>
                  <select className="w-full h-11 rounded-lg border border-input bg-background px-3 text-sm">
                    <option>75-100 words</option>
                    <option>100-150 words</option>
                    <option>150-200 words</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Default Language</Label>
                  <select className="w-full h-11 rounded-lg border border-input bg-background px-3 text-sm">
                    <option>English</option>
                    <option>Spanish</option>
                    <option>French</option>
                  </select>
                </div>
              </div>
              <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground">
                Save Defaults
              </Button>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="space-y-4">
              <h2 className="text-lg font-medium">Users & Access</h2>
              <p className="text-sm text-muted-foreground">
                Manage team members and their permissions.
              </p>
              <div className="border border-border rounded-xl p-6 sm:p-8 text-center">
                <p className="text-muted-foreground">No team members added yet</p>
                <Button variant="outline" className="mt-4">Invite Team Member</Button>
              </div>
            </div>
          )}

          {activeTab === 'subscription' && (
            <div className="space-y-4">
              <h2 className="text-lg font-medium">Subscription</h2>
              <div className="border border-border rounded-xl p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                  <div>
                    <p className="font-medium">Free Plan</p>
                    <p className="text-sm text-muted-foreground">10 reviews per month</p>
                  </div>
                  <span className="text-xs font-medium text-muted-foreground bg-muted px-3 py-1.5 rounded-lg self-start sm:self-auto">
                    CURRENT PLAN
                  </span>
                </div>
                <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground">
                  Upgrade to Pro
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
